import React, {useContext, useEffect} from 'react';
import {Link, withRouter} from 'react-router';

import {Heading, Icon, NoDataMsg, Text, Anchor, LoadingSpinner} from '@albert-io/atomic';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {CLASSROOM_STATUSES} from 'client/constants';
import sessionStore from 'client/Session/SessionStore';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/FeatureFlag.react';

import {ReportsContext} from '../Reports.context';
import {
  getClassroomsQuery,
  getClassroomQuery,
  isDimension,
  isDrillingDown,
  getTeacherQuery,
  getSchoolQuery,
  getDistrictQuery
} from '../reports.utils';

interface Props {
  children: React.ReactNode;
  router: any;
}

function ReportSelector({children, router}: Props) {
  const {topLevelFilterType, topLevelFilterId, path} = useContext(ReportsContext);
  const {isLoading: isAllClassesFeatureFlagLoading, isFeatureEnabled: isAllClassesFeatureEnabled} =
    useIsFeatureFlagEnabled('reports_teacher_id_filter');
  const classrooms = getClassroomsQuery().getResource();
  const classroomIsSelected = topLevelFilterType === 'classrooms' && !!topLevelFilterId;
  const teacherIsSelected = topLevelFilterType === 'teachers' && !!topLevelFilterId;
  const schoolIsSelected = topLevelFilterType === 'schools' && !!topLevelFilterId;
  const districtIsSelected = topLevelFilterType === 'districts' && !!topLevelFilterId;

  const queries = [getClassroomsQuery()];
  if (classroomIsSelected) {
    queries.push(getClassroomQuery(topLevelFilterId));
  } else if (teacherIsSelected) {
    queries.push(getTeacherQuery(topLevelFilterId));
  } else if (schoolIsSelected) {
    queries.push(getSchoolQuery(topLevelFilterId));
  } else if (districtIsSelected) {
    queries.push(getDistrictQuery(topLevelFilterId));
  }

  const selectedClassroom =
    classroomIsSelected && getClassroomQuery(topLevelFilterId).getResource();
  const selectedTeacher = teacherIsSelected && getTeacherQuery(topLevelFilterId).getResource();
  const selectedSchool = schoolIsSelected && getSchoolQuery(topLevelFilterId).getResource();
  const selectedDistrict = districtIsSelected && getDistrictQuery(topLevelFilterId).getResource();

  const dimensionIsSelected = path.some((item) => isDimension(item) || item === 'gradebook');

  useEffect(() => {
    if (isAllClassesFeatureFlagLoading || !topLevelFilterId) {
      return;
    }

    if (topLevelFilterType === 'teachers' && !isAllClassesFeatureEnabled) {
      router.push('/reports');
      return;
    }

    if (!dimensionIsSelected) {
      const pathnameWithoutTrailingSlash = router.location.pathname.replace(/\/+$/, '');
      if (topLevelFilterType === 'teachers') {
        router.push(`${pathnameWithoutTrailingSlash}/students`);
      } else {
        router.push(`${pathnameWithoutTrailingSlash}/gradebook`);
      }
    }
  }, [
    isAllClassesFeatureFlagLoading,
    isAllClassesFeatureEnabled,
    dimensionIsSelected,
    topLevelFilterType,
    topLevelFilterId,
    router
  ]);

  const {isReady} = useAreLatestQueriesReady(queries);

  if (!isReady || isAllClassesFeatureFlagLoading) {
    return <LoadingSpinner size={2} />;
  }

  if (
    isReady &&
    classrooms.isEmpty() &&
    !selectedClassroom &&
    !selectedTeacher &&
    !selectedSchool &&
    !selectedDistrict
  ) {
    return (
      <NoDataMsg.Container
        centered
        className='u-mar-lr_auto u-display_flex u-align-items_center u-flex-direction_column'
      >
        {/* @todo add illustration */}
        <NoDataMsg.Heading>You have no classes</NoDataMsg.Heading>
        <NoDataMsg.Body size='regular'>
          Creating a class is the first step to generating data to inform your instruction. You can
          create a class for each period or section you teach.
        </NoDataMsg.Body>
        <NoDataMsg.Button as={Link} to='/classes?createClassroomModal=true'>
          Create a class
        </NoDataMsg.Button>
      </NoDataMsg.Container>
    );
  }

  const archivedClassroomOptions: {id: string; value: string; label: string}[] = [];

  let activeClassroomOptions = classrooms.reduce((acc, classroom) => {
    const option = {
      label: classroom.getName(),
      value: classroom.getId(),
      id: classroom.getId()
    };
    if (classroom.getStatus() === CLASSROOM_STATUSES.ACTIVE) {
      acc.push(option);
    } else {
      archivedClassroomOptions.push({
        ...option,
        label: `${classroom.getName()} (Archived)`
      });
    }
    return acc;
  }, []);

  if (!activeClassroomOptions.length) {
    activeClassroomOptions = [
      {
        disabled: true,
        label: 'You have no active classes.',
        value: 'You have no active classes.',
        id: 'no-active-classes'
      }
    ];
  }

  const isOwnClassroom =
    topLevelFilterType === 'classrooms' &&
    [...activeClassroomOptions, ...archivedClassroomOptions].some(
      (option) => option.id === topLevelFilterId
    );

  let isShared = false;
  if (
    // is my classroom
    (selectedClassroom && !isOwnClassroom) ||
    // is filtered to my teacher ID
    (selectedTeacher && topLevelFilterId !== sessionStore.getUserId())
  ) {
    isShared = true;
  }

  return (
    <>
      <div className='u-display_flex u-flex-align_center u-flex-direction_column'>
        {isShared && (
          <div className='u-display_flex u-justify-content_center u-align-items_center u-border-radius_1 u-bgc_slate-100 u-color_slate-500 u-pad_1 u-mar-b_2'>
            <p className='u-mar-b_0 u-text-align_center'>
              <Icon className='u-mar-r_2' icon={['far', 'users']} />
              Shared report from{' '}
              <Text bold>
                {selectedClassroom
                  ? selectedClassroom.getName()
                  : `${selectedTeacher.getFirstName()} ${selectedTeacher.getLastName()}`}
              </Text>{' '}
              at <Text bold>{(selectedClassroom || selectedTeacher).getSchool().getName()}</Text>.
              <Anchor className='u-mar-l_1' to='/reports'>
                Exit shared report
              </Anchor>
            </p>
          </div>
        )}
        {!isDrillingDown(path) && isShared ? (
          <div className='u-mar-t_4 u-display_flex u-align-items_center reports_primary-dimension-header'>
            <Heading className='u-mar-r_3' as='span'>
              {selectedClassroom && selectedClassroom.getName()}

              {selectedTeacher &&
                `${selectedTeacher.getFirstName()} ${selectedTeacher.getLastName()}'s Classes`}
            </Heading>
          </div>
        ) : null}
        {!topLevelFilterId ? (
          <Text
            as='div'
            size='xl'
            className='u-display_flex u-flex-direction_column u-align-items_center u-mar-t_10 u-border-radius_2 u-bgc_slate-050 u-pad_6 u-mar-lr_8'
          >
            <Icon className='u-mar-b_2' icon='lightbulb-on' iconStyle='regular' size='inherit' />
            <Text size='regular'>Choose a class to get started.</Text>
          </Text>
        ) : null}
      </div>
      {children}
    </>
  );
}

export default withRouter(ReportSelector);
